import Navbar from "../Components/Header/Navbar";
import background from "../img/Background-header.jpg";
import ai from "../img/statistics2.jpg";
import headerphoto from "../img/Video-Player-Mockup.png";
import library from "../img/library.jpg";
import livemockup from "../img/livemockup.png";
import Footer from "../Components/Footer/Footer";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

export const WizardApp = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  return (
    <div className="overflow-x-hidden bg-white">
      {/* Hero Section */}
      <div className="relative">
        {/* Enhanced Background with Overlay */}
        <div
          className="fixed inset-0 z-0 bg-cover bg-center bg-no-repeat bg-fixed"
          style={{ backgroundImage: `url(${background})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-black/60 via-black/40 to-purple-900/30 z-5" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent z-5" />

        <div className="relative z-10">
          <Navbar />

          <div className="min-h-screen flex items-center justify-center px-4 sm:px-6 lg:px-8 pt-32 pb-32">
            <div className="container mx-auto">
              <div className="flex flex-col lg:flex-row items-center gap-16 lg:gap-20">
                {/* Enhanced Hero Content */}
                <div className="max-w-2xl text-center lg:text-left space-y-8 animate-fade-in-up">
                  {/* Badge */}

                  <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-white leading-[1.1] tracking-tight">
                    <span className="bg-gradient-to-r from-white via-purple-200 to-purple-300 bg-clip-text text-transparent">
                      {t("home.wizard_app.hero.title")}
                    </span>
                  </h1>

                  <div className="space-y-4">
                    <p className="text-xl sm:text-2xl text-white/90 leading-relaxed font-light">
                      {t("home.wizard_app.hero.description1")}
                    </p>
                    <p className="text-lg text-white/80 leading-relaxed">
                      {t("home.wizard_app.hero.description2")}
                    </p>
                  </div>

                  {/* Enhanced CTA Buttons */}
                  <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start pt-4">
                    <button
                      onClick={() => navigate("/pricing")}
                      className="group relative px-8 py-4 bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-white font-semibold rounded-2xl transition-all duration-300 shadow-xl hover:shadow-2xl transform hover:-translate-y-1 hover:scale-105"
                    >
                      <span className="relative z-10">
                        {t("home.wizard_app.hero.buttons.pricing")}
                      </span>
                      <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-2xl" />
                    </button>
                    <a
                      href="https://wizardfootball.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group px-8 py-4 border-2 border-white/30 bg-white/10 backdrop-blur-sm text-white font-semibold rounded-2xl transition-all duration-300 hover:bg-white/20 hover:border-white/50 transform hover:-translate-y-1 hover:scale-105"
                    >
                      <span className="flex items-center justify-center gap-2">
                        {t("home.wizard_app.hero.buttons.platform")}
                        <svg
                          className="w-4 h-4 group-hover:translate-x-1 transition-transform"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                          />
                        </svg>
                      </span>
                    </a>
                  </div>
                </div>

                {/* Enhanced Hero Image */}
                <div className="w-full max-w-2xl lg:max-w-xl animate-fade-in-right">
                  <div className="relative group">
                    <div className="absolute -inset-4 bg-gradient-to-r from-purple-600 to-blue-600 rounded-3xl blur-xl opacity-30 group-hover:opacity-50 transition-opacity duration-500" />
                    <img
                      src={headerphoto}
                      alt="Wizard App Preview"
                      className="relative rounded-3xl shadow-2xl transform group-hover:scale-105 transition-transform duration-500 border border-white/20"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Features Sections */}
      <div className="relative z-20 bg-white">
        {/* Library Feature */}
        <section className="py-24 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
          {/* Decorative Elements */}
          <div className="absolute top-0 left-0 w-72 h-72 bg-purple-100 rounded-full blur-3xl opacity-30 -translate-x-1/2 -translate-y-1/2" />

          <div className="container mx-auto max-w-7xl">
            <div className="flex flex-col lg:flex-row items-center gap-16 lg:gap-24">
              <div className="w-full lg:w-1/2 relative group">
                <div className="absolute -inset-8 bg-gradient-to-br from-purple-100 to-blue-100 rounded-3xl opacity-50 group-hover:opacity-70 transition-opacity duration-500" />
                <img
                  src={library}
                  className="relative rounded-3xl shadow-2xl w-full object-cover border border-gray-100 group-hover:shadow-3xl transition-shadow duration-500"
                  alt="Library Feature"
                />
              </div>

              <div className="w-full lg:w-1/2 space-y-8">
                <div className="space-y-4">
                  <div className="inline-flex items-center px-4 py-2 bg-purple-100 text-purple-700 rounded-full text-sm font-semibold">
                    📹 Video Feed
                  </div>
                  <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 leading-tight">
                    {t("home.wizard_app.library.title")}
                  </h2>
                </div>

                <div className="space-y-6">
                  <div className="flex items-start gap-4 p-4 bg-gray-50 rounded-2xl hover:bg-gray-100 transition-colors duration-300">
                    <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl flex items-center justify-center flex-shrink-0">
                      <svg
                        className="w-6 h-6 text-white"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    </div>
                    <p className="text-gray-700 text-lg leading-relaxed">
                      {t("home.wizard_app.library.paragraph1")}
                    </p>
                  </div>

                  <div className="flex items-start gap-4 p-4 bg-gray-50 rounded-2xl hover:bg-gray-100 transition-colors duration-300">
                    <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl flex items-center justify-center flex-shrink-0">
                      <svg
                        className="w-6 h-6 text-white"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M13 10V3L4 14h7v7l9-11h-7z"
                        />
                      </svg>
                    </div>
                    <p className="text-gray-700 text-lg leading-relaxed">
                      {t("home.wizard_app.library.paragraph2")}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Live Streaming Feature */}
        <section className="py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 to-gray-100 relative overflow-hidden">
          {/* Decorative Elements */}
          <div className="absolute bottom-0 right-0 w-72 h-72 bg-blue-100 rounded-full blur-3xl opacity-30 translate-x-1/2 translate-y-1/2" />

          <div className="container mx-auto max-w-7xl">
            <div className="flex flex-col lg:flex-row items-center gap-16 lg:gap-24">
              <div className="w-full lg:w-1/2 order-2 lg:order-1 space-y-8">
                <div className="space-y-4">
                  <div className="inline-flex items-center px-4 py-2 bg-red-100 text-red-700 rounded-full text-sm font-semibold">
                    🔴 Live Streaming
                  </div>
                  <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 leading-tight">
                    {t("home.wizard_app.live_streaming.title")}
                  </h2>
                </div>

                <div className="flex items-start gap-4 p-6 bg-white rounded-2xl shadow-lg hover:shadow-xl transition-shadow duration-300">
                  <div className="w-12 h-12 bg-gradient-to-br from-red-500 to-red-600 rounded-2xl flex items-center justify-center flex-shrink-0">
                    <svg
                      className="w-6 h-6 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
                      />
                    </svg>
                  </div>
                  <p className="text-gray-700 text-lg leading-relaxed">
                    {t("home.wizard_app.live_streaming.paragraph")}
                  </p>
                </div>
              </div>

              <div className="w-full lg:w-1/2 order-1 lg:order-2 relative group">
                <div className="absolute -inset-8 bg-gradient-to-br from-red-100 to-orange-100 rounded-3xl opacity-50 group-hover:opacity-70 transition-opacity duration-500" />
                <img
                  src={livemockup}
                  className="relative rounded-3xl shadow-2xl w-full border border-gray-100 group-hover:shadow-3xl transition-shadow duration-500"
                  alt="Live Streaming Feature"
                />
              </div>
            </div>
          </div>
        </section>

        {/* AI Statistics Feature */}
        <section className="py-24 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
          {/* Decorative Elements */}
          <div className="absolute top-0 left-1/2 w-96 h-96 bg-gradient-to-r from-purple-100 to-blue-100 rounded-full blur-3xl opacity-30 -translate-x-1/2 -translate-y-1/2" />

          <div className="container mx-auto max-w-7xl">
            <div className="flex flex-col lg:flex-row items-center gap-16 lg:gap-24">
              <div className="w-full lg:w-1/2 relative group">
                <div className="absolute -inset-8 bg-gradient-to-br from-blue-100 to-purple-100 rounded-3xl opacity-50 group-hover:opacity-70 transition-opacity duration-500" />
                <img
                  src={ai}
                  className="relative rounded-3xl shadow-2xl w-full object-cover border border-gray-100 group-hover:shadow-3xl transition-shadow duration-500"
                  alt="AI Statistics Feature"
                />
              </div>

              <div className="w-full lg:w-1/2 space-y-8">
                <div className="space-y-4">
                  <div className="inline-flex items-center px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-semibold">
                    🤖 AI Powered
                  </div>
                  <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 leading-tight">
                    {t("home.wizard_app.ai_statistics_model.title")}
                  </h2>
                </div>

                <div className="flex items-start gap-4 p-6 bg-gradient-to-br from-blue-50 to-purple-50 rounded-2xl border border-blue-100 hover:border-blue-200 transition-colors duration-300">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center flex-shrink-0">
                    <svg
                      className="w-6 h-6 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
                      />
                    </svg>
                  </div>
                  <p className="text-gray-700 text-lg leading-relaxed">
                    {t("home.wizard_app.ai_statistics_model.paragraph")}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>

      <Footer />
    </div>
  );
};
